import type { EndpointDTO, TargetDTO } from '@devrobot/shared';
import { Check, Pin, PinOff } from 'lucide-react';
import { pinnedByUrl, useAddShortcut, useRemoveShortcut, useShortcuts } from '../api/shortcuts';
import { MenuPopover, MenuSeparator } from './MenuPopover';
import { endpointLabels, endpointStatus, isBrowsable, orderHosts, type EndpointLabel, type EndpointTone } from '../lib/endpoints';

const LIGHT: Record<EndpointTone, string> = {
  success: 'status status-success',
  warning: 'status status-warning',
  error: 'status status-error',
  neutral: 'status status-neutral',
};

const NOTE = 'Shortcuts appear at the top of the sidebar. Only you can see them.';

/** One host, pinned or not. A native title rather than a tooltip, as in the Visit menu. */
function HostRow({ e, label, pinned, onToggle }: { e: EndpointDTO; label: EndpointLabel; pinned: boolean; onToggle: () => void }) {
  const s = endpointStatus(e);
  const name = s.detail ? `${s.label}. ${s.detail}` : s.label;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={pinned}
        title={e.url}
        className="flex w-full items-center gap-2 rounded-field px-2 py-1.5 text-left hover:bg-base-200/60"
      >
        <span className="inline-flex shrink-0" role="img" aria-label={name} title={name}>
          <span className={LIGHT[s.tone]} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-sm">
          <span className="font-medium">{label.lead}</span>
          <span className="text-base-content/50">{label.rest}</span>
        </span>
        {pinned ? <Check className="size-4 shrink-0 text-primary" aria-hidden="true" /> : <Pin className="size-4 shrink-0 opacity-40" aria-hidden="true" />}
      </button>
    </li>
  );
}

/**
 * Pins a deployment's hosts to this person's Shortcuts list in the sidebar. With several hosts it
 * drops down the same menu the Visit button uses, so pinning reads as a variant of visiting.
 *
 * Only route hosts can be pinned: a shortcut opens a browser tab, and a direct host port is
 * copy-only everywhere else too. The button stays available on a removed deployment, which is the
 * one place a shortcut to it can still be unpinned.
 */
export function AddToMenuButton({ target, className = '' }: { target: TargetDTO; className?: string }) {
  const shortcuts = useShortcuts();
  const addShortcut = useAddShortcut();
  const removeShortcut = useRemoveShortcut();
  const hosts = orderHosts(target.endpoints).filter(isBrowsable);
  const pinned = pinnedByUrl(shortcuts.data, target.id);
  const cls = `btn btn-ghost btn-sm ${className}`;

  if (hosts.length === 0) return null;

  const toggle = (url: string) => {
    const id = pinned.get(url);
    if (id) removeShortcut.mutate(id);
    else addShortcut.mutate({ targetId: target.id, url });
  };
  const error = addShortcut.error ?? removeShortcut.error;

  // A one-item menu is pure friction: make the button the toggle.
  if (hosts.length === 1) {
    const only = hosts[0];
    const on = pinned.has(only.url);
    return (
      <button type="button" className={cls} aria-pressed={on} onClick={() => toggle(only.url)} title={error?.message ?? NOTE}>
        {on ? <PinOff className="size-4" aria-hidden="true" /> : <Pin className="size-4" aria-hidden="true" />}
        {on ? 'In menu' : 'Add to menu'}
      </button>
    );
  }

  const labels = endpointLabels(hosts);
  return (
    <MenuPopover
      label={`Add hosts of ${target.project.name} · ${target.environment} to the menu`}
      triggerClassName={cls}
      panelClassName="w-80 max-w-[calc(100vw-2rem)]"
      align="start"
      trigger={
        <>
          <Pin className="size-4" aria-hidden="true" /> Add to menu
          {pinned.size > 0 ? <span className="badge badge-ghost badge-xs">{pinned.size}</span> : null}
        </>
      }
    >
      {/* The menu stays open on a click: pinning two of three hosts in one visit is the normal
          case, unlike Visit, where opening a tab ends the interaction. */}
      {() => (
        <>
          <ul>
            {hosts.map((e) => (
              <HostRow key={e.url} e={e} label={labels.get(e.url) ?? { lead: e.url, rest: '' }} pinned={pinned.has(e.url)} onToggle={() => toggle(e.url)} />
            ))}
          </ul>
          <MenuSeparator />
          <p className={`px-2 py-1 text-xs ${error ? 'dr-ink-error' : 'text-base-content/55'}`} role={error ? 'alert' : undefined}>
            {error?.message ?? NOTE}
          </p>
        </>
      )}
    </MenuPopover>
  );
}
